import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa6";
import { SocketContext } from "../context/SocketProvider";
import { useGameStage } from "../hooks/useGameStage";
import { useLanguage } from "../context/LanguageContext";
import { GAME_STAGES, HOME_PATH } from "../constants/gameStages";

// TODO: change graphics and colors

/**
 * A self-contained notification modal that appears when the other player leaves the game.
 * Handles its own socket listening, state management, and navigation.
 * Features keyboard navigation: ENTER to confirm.
 */
const PlayerLeftNotification = () => {
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();
  const { updateStage } = useGameStage();
  const { socket } = useContext(SocketContext);
  const { t } = useLanguage();

  // Handle when other player leaves the game
  useEffect(() => {
    const handlePlayerLeft = (data) => {
      const notificationMessage = t('playerLeft.message', "Your game partner has left the session. You will now be returned to the home screen.");
      setMessage(notificationMessage);
    };

    socket.on("player_left_game", handlePlayerLeft);

    return () => {
      socket.off("player_left_game", handlePlayerLeft);
    };
  }, [socket]);

  // Handle player left notification confirmation
  const handleConfirm = () => {
    setMessage(null);
    updateStage(GAME_STAGES.READY);
    navigate(HOME_PATH);
  };

  // Handle keyboard navigation - must be called before early return
  useEffect(() => {
    // Only add event listener if message exists
    if (!message) return;

    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    
    // Auto-focus the OK button when modal opens
    const okButton = document.querySelector('.player-left-ok-button');
    if (okButton) {
      okButton.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [message]);

  if (!message) return null;

  return (
    <div 
      className="fixed inset-0 z-50 w-full h-full bg-black/30 overflow-hidden flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <article className="w-[514px] h-56 rounded-2xl overflow-hidden relative bg-white">
        <header className="w-[514px] h-16 bg-yellow400 flex items-center justify-center">
          <h2 className="text-center text-white text-xl font-bold font-['Gabarito'] px-4">
            {t('playerLeft.title', 'Player Left Game')}
          </h2>
        </header>
        <main className="w-[514px] h-40 bg-white relative flex flex-col items-center justify-center p-4">
          <p className="text-center text-gray-800 text-lg">
            {message}
          </p>
          <button
            className="btn-yellow-sm relative mb-[4px] mt-[16px] player-left-ok-button"
            onClick={handleConfirm}
          >
            <span className="btn-text-pos-right-sm">{t('button.ok', 'OK')}</span>
            <FaChevronRight className="arrow-pos-right-sm" />
          </button>
        </main>
      </article>
    </div>
  );
};

export default PlayerLeftNotification;
